import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import type { SerializedObject, UnpackResponse } from "../../../api/src/lib";
import type { RebalancesController } from "../../../api/src/rebalances/rebalances.controller";
import { useAuthenticatedFetchAllPages } from "../lib/pagination";

/**
 * The published path of `getRebalanceBoards`. The four rebalance-board
 * handlers serve at `/v1` only (APP-1652): `POST` to this path, `PUT` and
 * `DELETE` to `${REBALANCE_BOARDS_PATH}/:id`. It is also the FIRST element of
 * every react-query key the walk below uses.
 */
export const REBALANCE_BOARDS_PATH = "/v1/rebalances/boards";

/** One item of `GET /v1/rebalances/boards`, as the route serialises it. */
export type RebalanceBoard = SerializedObject<
  UnpackResponse<RebalancesController["getRebalanceBoards"]>
>["data"][number];

/**
 * Every rebalance board of the caller's firm. `GET /v1/rebalances/boards`
 * answers a page (APP-1652), and `BoardManagementModal.tsx`, `BoardSelector.tsx`
 * and `RebalanceQueryControls.tsx` each show or search every board, so this
 * walks every page (`useAuthenticatedFetchAllPages`) and holds the list back
 * until the last one is in.
 *
 * Once a list is complete it stays in `boards` while a `reload` walks again,
 * so a screen does not drop to its loading state after each save.
 *
 * `reload` is for a screen that WRITES a board, or is told one was written.
 * It removes the cached pages before it restarts the walk: invalidating them
 * is not enough, because no observer is left on the first page once the walk
 * has finished, so the restarted walk would be handed the cached page, list
 * the boards as they were before the save, and stop there.
 */
export function useRebalanceBoards() {
  const queryClient = useQueryClient();
  const { rows, hasEveryRow, isError, reload } =
    useAuthenticatedFetchAllPages<RebalanceBoard>(REBALANCE_BOARDS_PATH);

  const [lastCompleteBoards, setLastCompleteBoards] = useState<
    RebalanceBoard[] | null
  >(null);

  useEffect(() => {
    if (hasEveryRow) {
      setLastCompleteBoards(rows);
    }
  }, [hasEveryRow, rows]);

  const boards = hasEveryRow ? rows : (lastCompleteBoards ?? []);

  return {
    /** Every board, or `[]` until the first walk is complete. */
    boards,
    /** True until the first walk is complete, unless it failed. */
    isPending: !hasEveryRow && lastCompleteBoards === null && !isError,
    isError,
    reload: useCallback(() => {
      queryClient.removeQueries({ queryKey: [REBALANCE_BOARDS_PATH] });
      reload();
    }, [queryClient, reload]),
  };
}
